import type { CommandBridge, CommandHandler, CommandPayload } from '@/bridge/CommandBridge'
import {
    Abort,
    createLogPrefix,
    createShortUid,
    Deferred,
    type Disposable,
    ErrorLevel,
    ErrorOrigin,
    type Fun,
    IllegalStateError,
    logError,
    type LogTarget,
    logWarn,
    ReportableError,
    type Unsubscribe,
    withAbort,
    withTimeout,
} from '@amazon/vinyl-util'

// Fire-and-forget
const SEND = 'send'
// Send, expect a response
const REQUEST = 'request'
// Respond to a request
const RESOLVE = 'resolve'
// A request had no handler registered
const REJECT = 'reject'

export interface BridgeControllerOptions {
    readonly timeout?: number
}

interface SendCommand extends CommandPayload {
    readonly type: typeof SEND | typeof REQUEST
    readonly payload: {
        readonly prop: string
        readonly params: any[]
    }
}

interface ResolveCommand extends CommandPayload {
    readonly type: typeof RESOLVE
    readonly payload: {
        readonly prop: string
        readonly response: any
    }
}

interface RejectCommand extends CommandPayload {
    readonly type: typeof REJECT
    readonly payload: {
        readonly name: string
        readonly prop: string
        readonly level: ErrorLevel
        readonly origin: string
        readonly message: string
    }
}

type BridgeCommand = SendCommand | ResolveCommand | RejectCommand

export interface BridgeControllerDeps {
    readonly bridge: CommandBridge
}

/**
 * Wraps a command bridge, providing a protocol for issuing fire-and-forget commands or commands
 * that require responses..
 */
export class BridgeController implements Disposable, LogTarget {
    readonly logPrefix = createLogPrefix('BridgeController')

    private bridgeSub: Unsubscribe | null
    private readonly bridge: CommandBridge

    private readonly handlers = new Map<string, Fun>()
    private readonly pending = new Map<string, Deferred<any>>()
    private readonly abort = new Abort()

    constructor(
        deps: BridgeControllerDeps,
        private readonly options?: BridgeControllerOptions
    ) {
        this.bridge = deps.bridge
        this.bridgeSub = deps.bridge.listen(this.createBridgeHandler())
    }

    private createBridgeHandler(): CommandHandler {
        return (commandPayload) => {
            const { payload, id, type } = commandPayload as BridgeCommand

            switch (type) {
                case SEND: {
                    const handler = this.handlers.get(payload.prop)
                    if (!handler) {
                        logWarn(this, 'unhandled command:', payload.prop)
                        return
                    }
                    handler(...payload.params)
                    break
                }
                case REQUEST: {
                    // Requires a response
                    const handler = this.handlers.get(payload.prop)
                    if (!handler) {
                        // No handler was registered, fail the request
                        this.send({
                            type: REJECT,
                            payload: {
                                name: 'unhandled',
                                prop: payload.prop,
                                level: ErrorLevel.WARN,
                                origin: ErrorOrigin.INTERNAL,
                                message: `${payload.prop} had no handlers`,
                            },
                            id,
                        })
                        return
                    }
                    ;(async () => {
                        const response = await withAbort(handler(...payload.params), this.abort)
                        this.send({
                            type: RESOLVE,
                            payload: {
                                response,
                                prop: payload.prop,
                            },
                            id,
                        })
                    })().catch((error: Error) => {
                        let level: ErrorLevel, origin: string
                        if (error instanceof ReportableError) {
                            level = error.level
                            origin = error.origin
                        } else {
                            level = ErrorLevel.FATAL
                            origin = ErrorOrigin.INTERNAL
                        }

                        if (this.bridge.canSend()) {
                            this.send({
                                type: REJECT,
                                payload: {
                                    name: error.name,
                                    prop: payload.prop,
                                    level,
                                    origin,
                                    message: error.message,
                                },
                                id,
                            })
                        }
                    })
                    break
                }

                case RESOLVE: {
                    const pending = this.pending.get(id)
                    if (!pending) {
                        logError(this, 'missing pending resolve promise')
                        return
                    }
                    pending.resolve(payload.response)
                    break
                }

                case REJECT: {
                    const pending = this.pending.get(id)
                    if (!pending) {
                        logError(this, 'missing pending resolve promise')
                        return
                    }
                    pending.reject(
                        new BridgeResponseError(payload.message, payload.origin, payload.level)
                    )
                    break
                }
            }
        }
    }

    private send(command: BridgeCommand) {
        if (!this.bridge.canSend())
            throw new IllegalStateError(
                `not connected, cannot send ${command.type}: ${command.payload.prop}`
            )
        this.bridge.send(command)
    }

    setHandler(key: string, handler: Fun): Unsubscribe {
        this.handlers.set(key, handler)
        return () => {
            this.handlers.delete(key)
        }
    }

    /**
     * Returns a function that when invoked, sends a SEND command over the bridge.
     * This is fire-and-forget and does not expect a response.
     */
    createCommand(prop: string): (...args: any[]) => void {
        return ((...params: any[]): void => {
            this.send({
                type: SEND,
                payload: {
                    prop: prop,
                    params,
                },
                id: createShortUid(),
            })
        }) as any
    }

    /**
     * Creates a command that expects a response.
     *
     * A handler must be added on the receiver with the function name which
     * returns the response.
     *
     * @param prop
     * @param timeout
     */
    createAsyncCommand(
        prop: string,
        timeout = this.options?.timeout ?? 30
    ): (...args: any[]) => Promise<any> {
        return (...params: any[]): Promise<any> => {
            const id = createShortUid()
            const deferred = new Deferred()
            const promise = withTimeout(
                withAbort(deferred, this.abort),
                timeout,
                `bridge async command '${prop}' timed out after {0}s.`
            ).finally(() => {
                this.pending.delete(id)
            })
            this.pending.set(id, deferred)

            console.log('sending request:', prop)
            this.send({
                type: REQUEST,
                payload: {
                    prop,
                    params,
                },
                id,
            })
            return promise
        }
    }

    dispose() {
        this.abort.abort()
        this.bridgeSub?.()
        this.bridgeSub = null
        this.pending.clear()
    }
}

export class BridgeResponseError extends ReportableError {
    get [Symbol.toStringTag](): string {
        return 'BridgeResponseError'
    }
}
